import React, { useState, type JSX } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useApi } from '@/hooks/useAppApi'
import { usePermissions } from '@/hooks/usePermissions'
import { useLogout } from '@/features/auth/components/LogoutButton'
import {
  Settings,
  Info,
  HelpCircle,
  LogOut,
  ChevronDown,
  MessageSquare,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { isSupportBundlesEnabled } from '@/features/support-bundles/enabled'
import { openSendBundleDialog } from '@/features/support-bundles/SupportBundleHost'

/**
 * User info display within the dropdown menu
 */
const UserInfo = (): JSX.Element => {
  const { user } = useApi()

  if (!user) return <></>

  // Get initials from name for avatar fallback
  const initials = user.name
    ? user.name
        .split(' ')
        .map((n): string | undefined => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U'

  return (
    <div className="flex items-center gap-3 px-2 py-3 border-b">
      <Avatar className="h-10 w-10">
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{user.name || 'User'}</div>
        <div className="text-xs text-muted-foreground truncate">{user.email || ''}</div>
      </div>
    </div>
  )
}

interface MenuItemProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
  onClick: () => void
  variant?: 'default' | 'destructive'
}

/**
 * Individual menu item component
 */
const MenuItem = ({ icon: Icon, label, onClick, variant = 'default' }: MenuItemProps): JSX.Element => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-2 py-2 text-sm rounded-md transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:bg-accent focus:text-accent-foreground focus:outline-none',
        variant === 'destructive' && 'text-destructive hover:text-destructive'
      )}
      role="menuitem"
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1 text-left">{label}</span>
    </button>
  )
}

/**
 * Check if user has admin permissions (uses same wildcard-aware check as NavItem)
 */
function useHasAdminAccess(): boolean {
  const { hasAnyPermission } = usePermissions()
  return hasAnyPermission(['admin:*'])
}

/**
 * Enhanced user menu dropdown with common navigation items
 *
 * Includes:
 * - User profile information
 * - Admin (conditional on permissions)
 * - About
 * - Help/Support (opens the Revalize support site in a new tab)
 * - Logout
 */
export function UserMenuDropdown(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const hasAdminAccess = useHasAdminAccess()
  const { triggerLogout, isLoading: isLoggingOut } = useLogout()
  const { user } = useApi()

  // Get display name for the trigger button
  const displayName = user?.name?.split(' ')[0] || 'Account'

  // Close dropdown when clicking outside
  const handleBackdropClick = (): void => {
    setIsOpen(false)
  }

  // Handle navigation items
  const handleAbout = (): void => {
    setIsOpen(false)
    navigate('/about')
  }

  const handleAdmin = (): void => {
    setIsOpen(false)
    navigate('/admin')
  }

  const handleSendDiagnostics = (): void => {
    setIsOpen(false)
    openSendBundleDialog()
  }

  const handleHelp = (): void => {
    setIsOpen(false)
    window.open('https://revalizesoftware.com/revalize-support/', '_blank', 'noopener,noreferrer')
  }

  const handleLogout = async (): Promise<void> => {
    setIsOpen(false)
    await triggerLogout()
  }

  return (
    <div className="relative">
      {/* Trigger Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <Avatar className="h-6 w-6">
          <AvatarFallback className="text-xs">
            {user?.name
              ? user.name
                  .split(' ')
                  .map((n): string | undefined => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)
              : 'U'}
          </AvatarFallback>
        </Avatar>
        <span className="hidden sm:inline">{displayName}</span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop for closing dropdown */}
          <div className="fixed inset-0 z-40" onClick={handleBackdropClick} />

          {/* Menu Content */}
          <div
            className="absolute right-0 top-full mt-2 z-50 min-w-[240px] bg-popover border rounded-md shadow-lg"
            role="menu"
            aria-orientation="vertical"
          >
            <div className="p-2">
              {/* User Info */}
              <UserInfo />

              {/* Menu Items */}
              <div className="py-2 space-y-1">
                {hasAdminAccess && (
                  <MenuItem
                    icon={Settings}
                    label="Admin"
                    onClick={handleAdmin}
                  />
                )}

                <MenuItem
                  icon={Info}
                  label="About"
                  onClick={handleAbout}
                />

                <MenuItem
                  icon={HelpCircle}
                  label="Help / Support"
                  onClick={handleHelp}
                />

                {/* Mouse-discoverable equivalent of Ctrl+Shift+B. Gated by the
                    same predicate the shortcut uses — a second, narrower copy
                    is how you end up with a menu that hides an affordance the
                    hotkey still offers. */}
                {isSupportBundlesEnabled() && (
                  <MenuItem
                    // A speech bubble, not a bug. The dialog invites praise and
                    // suggestions as well as faults; a bug glyph beside the
                    // label tells the reader it is for defects, and quietly
                    // decides what they bother reporting. Matches the floating
                    // button, which is the same action.
                    icon={MessageSquare}
                    label="Send feedback"
                    onClick={handleSendDiagnostics}
                  />
                )}

                {/* Separator */}
                <div className="border-t my-2" />

                <MenuItem
                  icon={LogOut}
                  label={isLoggingOut ? 'Signing Out...' : 'Logout'}
                  onClick={handleLogout}
                  variant="destructive"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
